import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { LscArchiveItem } from '../types';
import {
  ARCHIVE_PLAYBACK_UNAVAILABLE_BODY,
  ARCHIVE_PLAYBACK_UNAVAILABLE_TITLE,
  TWITCH_VOD_EXPIRATION_COPY,
  TWITCH_VOD_UNAVAILABLE_COPY,
} from '../archiveCopy';
import { isArchivePlaybackUnavailable } from '../archivePlayback';
import { parseTwitchVodId } from '../twitchEmbed';
import TwitchVodEmbed from './TwitchVodEmbed';
import {
  YouTubeArchiveFrame,
  isYouTubeArchive,
  youtubeWatchUrl,
} from './YouTubeArchiveFrame';
import ArchiveCopyLinkButton from './ArchiveCopyLinkButton';
import { useLeague } from '../hooks/useLeague';
import { formatArchiveCardMeta, getArchiveDisplayTitle } from './leagueHome/leagueHomeUtils';

export function getArchiveWatchVodVideoId(archive: LscArchiveItem): string | null {
  return (
    archive.provider_vod_id?.trim() ||
    parseTwitchVodId(archive.url) ||
    parseTwitchVodId(archive.embed_url) ||
    null
  );
}

function isPublishedArchive(archive: LscArchiveItem): boolean {
  return archive.archive_status === 'published';
}

function formatPublishedDate(value?: string | null): string | null {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

interface ArchiveWatchVodSectionProps {
  archive: LscArchiveItem;
  leagueId: string;
  /** Stream watch renders its own title and utility row under the player. */
  playerOnly?: boolean;
}

function ArchiveVodActions({
  leagueId,
  archive,
  twitchUrl,
  showTwitchLink,
  linkLabel = 'Watch on Twitch',
  linkTestId = 'archive-vod-watch-on-twitch',
}: {
  leagueId: string;
  archive: LscArchiveItem;
  twitchUrl?: string | null;
  showTwitchLink: boolean;
  linkLabel?: string;
  linkTestId?: string;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-4"
      data-testid="archive-vod-actions"
    >
      {showTwitchLink && twitchUrl ? (
        <a
          href={twitchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-primary-300 hover:text-primary-200 text-sm font-medium"
          data-testid={linkTestId}
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
          {linkLabel}
        </a>
      ) : null}
      {isPublishedArchive(archive) ? (
        <ArchiveCopyLinkButton
          leagueId={leagueId}
          archiveId={archive.id}
          label="Copy replay link"
        />
      ) : null}
    </div>
  );
}

const ArchiveWatchVodSection: React.FC<ArchiveWatchVodSectionProps> = ({
  archive,
  leagueId,
  playerOnly = false,
}) => {
  const { league } = useLeague();
  const youtube = isYouTubeArchive(archive);
  const youtubeId = archive.provider_vod_id?.trim() || '';
  const videoId = getArchiveWatchVodVideoId(archive);
  const twitchUrl = archive.url?.trim() || null;
  const embedTitle = getArchiveDisplayTitle(archive);
  const playbackUnavailable = isArchivePlaybackUnavailable(archive);
  const showEmbed = Boolean(!playbackUnavailable && (videoId || twitchUrl));
  const metadata = formatArchiveCardMeta(archive, league?.active_edition);
  const publishedDate = formatPublishedDate(archive.published_at);

  return (
    <section
      className="space-y-4"
      data-testid="archive-watch-vod-section"
      id="replay-player"
    >
      <h2 className="sr-only">Replay</h2>

      {youtube && youtubeId ? (
        <>
          <div className="max-w-full overflow-hidden rounded-xl border border-secondary-700 bg-black/20">
            <YouTubeArchiveFrame videoId={youtubeId} title={embedTitle} />
          </div>
          {playerOnly ? null : (
            <ArchiveVodActions
              leagueId={leagueId}
              archive={archive}
              twitchUrl={youtubeWatchUrl(archive)}
              showTwitchLink
              linkLabel="Watch on YouTube"
              linkTestId="archive-vod-watch-on-youtube"
            />
          )}
        </>
      ) : playbackUnavailable ? (
        <div
          className="rounded-xl border border-secondary-700 bg-secondary-800/50 p-5 space-y-3"
          data-testid="archive-watch-vod-unavailable"
        >
          <p className="font-medium text-white">{ARCHIVE_PLAYBACK_UNAVAILABLE_TITLE}</p>
          <p className="text-sm text-secondary-400">{ARCHIVE_PLAYBACK_UNAVAILABLE_BODY}</p>
          <dl className="grid gap-2 text-sm text-secondary-300">
            <div>
              <dt className="text-secondary-500">Game</dt>
              <dd>{embedTitle}</dd>
            </div>
            {metadata ? (
              <div>
                <dt className="text-secondary-500">Details</dt>
                <dd>{metadata}</dd>
              </div>
            ) : null}
            {publishedDate ? (
              <div>
                <dt className="text-secondary-500">Published</dt>
                <dd>{publishedDate}</dd>
              </div>
            ) : null}
          </dl>
          {playerOnly ? null : (
            <ArchiveVodActions
              leagueId={leagueId}
              archive={archive}
              twitchUrl={twitchUrl}
              showTwitchLink={false}
            />
          )}
        </div>
      ) : showEmbed ? (
        <>
          <div className="max-w-full overflow-hidden rounded-xl border border-secondary-700 bg-black/20">
            <TwitchVodEmbed
              providerVodId={videoId ?? archive.provider_vod_id}
              embedUrl={archive.embed_url}
              embedParentDomains={archive.embed_parent_domains}
              title={embedTitle}
              thumbnailUrl={archive.thumbnail_url}
              twitchUrl={twitchUrl}
              showExternalLink={false}
            />
          </div>
          {playerOnly ? null : (
            <ArchiveVodActions
              leagueId={leagueId}
              archive={archive}
              twitchUrl={twitchUrl}
              showTwitchLink
            />
          )}
        </>
      ) : (
        <div
          className="rounded-xl border border-secondary-700 bg-secondary-800/50 p-4 space-y-4"
          data-testid="archive-watch-vod-unavailable"
        >
          <div>
            <p className="font-medium text-white">{ARCHIVE_PLAYBACK_UNAVAILABLE_TITLE}</p>
            <p className="mt-1 text-sm text-secondary-400">{TWITCH_VOD_UNAVAILABLE_COPY}</p>
          </div>
          {playerOnly ? null : (
            <ArchiveVodActions
              leagueId={leagueId}
              archive={archive}
              twitchUrl={twitchUrl}
              showTwitchLink={Boolean(twitchUrl)}
            />
          )}
        </div>
      )}

      {playerOnly || youtube ? null : (
        <p className="text-xs text-secondary-500" data-testid="twitch-vod-expiration-copy">
          {TWITCH_VOD_EXPIRATION_COPY}
        </p>
      )}
    </section>
  );
};

export default ArchiveWatchVodSection;
